import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, AlertCircle, XCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FlowResultPage({ user, onLogout }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const commerceOrder = searchParams.get("order");

  useEffect(() => {
    if (commerceOrder) {
      checkPaymentStatus();
    }
  }, [commerceOrder]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus("pending");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/flow/payment-status/${commerceOrder}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error("Failed to check payment status");
      }

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.payment_status === "rejected") {
        setStatus("rejected");
        return;
      } else if (data.payment_status === "cancelled") {
        setStatus("cancelled");
        return;
      }

      // Continue polling for pending
      setPollCount(prev => prev + 1);
      setTimeout(checkPaymentStatus, pollInterval);

    } catch (error) {
      // Error handled silently
      setStatus("error");
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "checking":
        return <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />;
      case "success":
        return <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />;
      case "pending":
        return <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4" />;
      case "rejected":
        return <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />;
      case "cancelled":
        return <XCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />;
      default:
        return <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case "checking":
        return "Verificando pago...";
      case "success":
        return "¡Pago Exitoso!";
      case "pending":
        return "Pago en Proceso";
      case "rejected":
        return "Pago Rechazado";
      case "cancelled":
        return "Pago Cancelado";
      default:
        return "Error en el Pago";
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case "checking":
        return "Estamos verificando tu pago con Flow, por favor espera...";
      case "success":
        return `Tu suscripción al plan ${paymentData?.plan_id?.toUpperCase() || "PRO"} ha sido activada correctamente.`;
      case "pending":
        return "Tu pago está siendo procesado. Puede tomar unos minutos en confirmarse.";
      case "rejected":
        return "Tu pago fue rechazado. Por favor, intenta con otro medio de pago.";
      case "cancelled":
        return "Has cancelado el proceso de pago. No se ha realizado ningún cargo.";
      default:
        return "Hubo un problema verificando tu pago. Por favor, contacta a soporte.";
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="bg-gray-900/50 border-gray-700 max-w-md w-full" data-testid="flow-result-card">
          <CardHeader className="text-center">
            {getStatusIcon()}
            <CardTitle className="text-white">{getStatusTitle()}</CardTitle>
          </CardHeader>
          
          <CardContent className="text-center space-y-4">
            <p className="text-gray-400">{getStatusMessage()}</p>
            
            {status === "success" && paymentData && (
              <div className="bg-gray-800 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-400">Monto pagado</p>
                <p className="text-2xl font-bold text-white">
                  ${paymentData.amount?.toLocaleString("es-CL")} CLP
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Orden: {commerceOrder}
                </p>
              </div>
            )}
            
            <div className="flex flex-col gap-2 mt-6">
              <Button
                onClick={() => navigate("/")}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="go-to-dashboard-btn"
              >
                Ir al Dashboard
              </Button>
              
              {(status === "rejected" || status === "cancelled" || status === "error") && (
                <Button
                  onClick={() => navigate("/subscription")}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  Intentar Nuevamente
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
